import { useState } from "react";
import { Card, CardContent, CardFooter, CardHeader } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ShoppingCart, ImageIcon, Plus, Minus, Box, Package } from "lucide-react";
import { useCart } from "../context/CartContext";

export function ProductCatalogCard({ product, channelDiscount = 0 }) {
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState("pz"); // "pz" or "ct"

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  // Formattazione speciale per contrassegno con 3 decimali
  const formatStampDuty = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(value);
  };

  // Calcola il prezzo scontato in base al canale
  const getDiscountedPrice = () => {
    if (channelDiscount > 0) {
      return product.commercial_price * (1 - channelDiscount / 100);
    }
    return product.commercial_price;
  };

  const displayPrice = getDiscountedPrice();

  const handleAddToCart = () => {
    // Calculate total pieces if unit is carton
    const finalQuantity = unit === "ct" ? quantity * (product.pieces_per_box || 1) : quantity;

    // Pass excise/stamp/vat data to cart context
    addToCart({
      ...product,
      excise_duty: product.excise_duty || 0,
      stamp_duty: product.stamp_duty || 0,
      vat_rate: product.vat_rate || 22.0
    }, finalQuantity);
    
    setQuantity(1); 
  };

  const increment = () => setQuantity(q => q + 1);
  const decrement = () => setQuantity(q => Math.max(1, q - 1));

  return (
    <Card className="overflow-hidden flex flex-col h-full hover:shadow-lg transition-shadow duration-300">
      <div className="relative aspect-square bg-muted flex items-center justify-center overflow-hidden">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            onError={(e) => {
              e.target.onerror = null; 
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className={`w-full h-full flex items-center justify-center bg-slate-100 text-slate-400 ${product.image_url ? 'hidden' : ''}`}>
          <ImageIcon className="w-12 h-12" />
        </div>
        <div className="absolute top-2 right-2">
          <Badge variant="secondary" className="bg-white/90 text-slate-800 backdrop-blur-sm shadow-sm">
            {product.category}
          </Badge>
        </div>
      </div>
      
      <CardHeader className="p-4 pb-2">
        <div className="flex justify-between items-start gap-2">
          <div>
            <h3 className="font-bold text-lg leading-tight line-clamp-2" title={product.name}>
              {product.name}
            </h3>
            <p className="text-xs text-muted-foreground font-mono mt-1">{product.code}</p>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-4 pt-2 flex-grow">
        <div className="flex items-end gap-2">
          {channelDiscount > 0 ? (
            <div className="flex flex-col">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-accent">
                  {formatCurrency(displayPrice)}
                </span>
                <span className="text-sm line-through text-muted-foreground">
                  {formatCurrency(product.commercial_price)}
                </span>
              </div>
              <span className="text-xs text-green-600 font-medium">
                Sconto {channelDiscount}% già applicato
              </span>
            </div>
          ) : (
            <span className="text-2xl font-bold text-accent">
              {formatCurrency(product.commercial_price)}
            </span>
          )}
          <span className="text-xs text-muted-foreground mb-1">
            / {product.unit_type === 'cartoni' ? 'cart.' : 'pz'} (conf. da {product.pieces_per_box})
          </span>
        </div>
        
        <div className="mt-2 space-y-1">
          {product.excise_duty > 0 && (
            <p className="text-xs text-muted-foreground flex justify-between">
              <span>Accisa:</span> <span>{formatCurrency(product.excise_duty)}</span>
            </p>
          )}
          {product.stamp_duty > 0 && (
            <p className="text-xs text-muted-foreground flex justify-between">
              <span>Contrassegno:</span> <span>{formatStampDuty(product.stamp_duty)}</span>
            </p>
          )}
          <p className="text-xs text-muted-foreground flex justify-between pt-1 border-t border-dashed">
             <span>IVA:</span> <span>{product.vat_rate || 22}%</span>
          </p>
        </div>
      </CardContent>

      <CardFooter className="p-4 pt-0 flex flex-col gap-3">
        {/* Unit Selector */}
        <div className="w-full grid grid-cols-2 gap-1 bg-muted p-1 rounded-md">
          <button
            onClick={() => setUnit("pz")}
            className={`text-xs font-medium py-1 rounded-sm transition-all flex items-center justify-center gap-1 ${
              unit === "pz" ? "bg-white shadow text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Package className="w-3 h-3" /> Pezzo
          </button>
          <button
            onClick={() => setUnit("ct")}
            className={`text-xs font-medium py-1 rounded-sm transition-all flex items-center justify-center gap-1 ${
              unit === "ct" ? "bg-white shadow text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Box className="w-3 h-3" /> Cartone
          </button>
        </div>

        <div className="flex gap-2 w-full">
          <div className="flex items-center border rounded-md">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 rounded-none" 
              onClick={decrement}
            >
              <Minus className="w-3 h-3" />
            </Button>
            <Input 
              type="number" 
              min="1" 
              className="w-12 h-9 text-center border-0 focus-visible:ring-0 px-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            />
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 rounded-none" 
              onClick={increment}
            >
              <Plus className="w-3 h-3" />
            </Button>
          </div>
          <Button 
            className="flex-1 bg-slate-900 hover:bg-slate-800"
            onClick={handleAddToCart}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {unit === "ct" ? `Aggiungi (${quantity * (product.pieces_per_box || 1)} pz)` : "Aggiungi"}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
